import { useMemo } from 'react'
import { useSuspenseQueries } from '@tanstack/react-query'

import {
  getAllBillingPeriodsOptions,
  getBilledAmountHistoryOptions,
  getInvoiceDetailOptions,
} from '@/api/main/@tanstack/react-query.gen'
import { getTimezone } from '@/utils/datetime'
import { computeBillingForecast, todayKey } from './billingForecast'
import { aggregateInvoiceRowsByDistrict } from './billingBreakdown'
import { sortPeriodsNewestFirst } from './billingPeriods'

export const billingPeriodQueries = (billingPeriodId: number) => {
  const options = { path: { billing_period_id: billingPeriodId } }
  return {
    history: getBilledAmountHistoryOptions(options),
    invoice: getInvoiceDetailOptions(options),
  }
}

export const useBillingOverview = (billingPeriodId: number) => {
  const queries = billingPeriodQueries(billingPeriodId)
  // Fetched in parallel; separate useSuspenseQuery calls would waterfall.
  const [{ data: periods }, { data: history }, { data: invoice }] = useSuspenseQueries({
    queries: [getAllBillingPeriodsOptions(), queries.history, queries.invoice],
  })

  const timezone = getTimezone()
  // Re-evaluated on every render (e.g. on refetch-on-focus) so "today" never goes stale.
  const now = new Date()
  const today = todayKey(now, timezone)

  const forecast = useMemo(
    () => computeBillingForecast({ history, orders: invoice.orders, prices: invoice.prices, now, timezone }),
    // `today` stands in for `now`: the forecast only changes when the day does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [history, invoice, timezone, today]
  )
  const districts = useMemo(() => aggregateInvoiceRowsByDistrict(invoice.invoice_rows), [invoice])
  const sortedPeriods = useMemo(() => sortPeriodsNewestFirst(periods), [periods])
  const period = periods.find(item => item.id === billingPeriodId) ?? null

  return { forecast, districts, periods: sortedPeriods, period, timezone, hasHistory: history.length > 0 }
}
